const {Component} = require("react");
const PropTypes = require("prop-types");
const store = require("stores/BookingsStore");
const userStore = require("stores/UserStore");
const Booking = require("components/backend/bookings/Booking");
const {classList} = require("helpers/html");
const {decodeObservable} = require("helpers/object");
const {observer} = require("mobx-react");
const {computed} = require("mobx");
const router = require("services/router");
const Modal = require("components/modals/Modal");
const DatePicker = require("components/forms/Date");
const Select = require("components/forms/Select");
const Textarea = require("components/forms/Textarea");
const slotStore = require("stores/HourSlotsStore");
const moment = require("moment");
const {copyTime} = require("helpers/moment");
const Confirm = require("components/modals/Confirm");
const {moneyFormat} = require("helpers/format");

@observer
class BookingList extends Component {

	static get propTypes() {
		return {
			viewAs: PropTypes.string
		};
	}

	static get defaultProps() {
		return {
			viewAs: "bookable"
		};
	}

	constructor(props) {
		super(props);

		this.state = {
			selectedBooking: null,
			dateChange: null,
			timeChange: null,
			messageChange: null,
			timeOptions: [],
			changeModalLoading: false,
			changeModalError: null,
			timeOptionsLoading: false,
			filter: this.props.viewAs === "bookable" ? "received" : "sent"
		};
	}

	get className() {
		return classList(
			"all-bookings",
			this.props.viewAs === "teacher" ? "as-teacher" : "as-student"
		);
	}

	getBooking(id) {
		return this.collection.find(booking => booking.id === id);
	}

	getBookingViewAs(booking) {
		return booking.booker.id === userStore.get("id") ?
			"booker" :
			"bookable";
	}

	getBookingPerson(booking) {
		return booking.booker.id === userStore.get("id") ?
			booking.bookable : booking.booker;
	}

	getTimeOptions() {
		if (!this.state.selectedBooking) return [];

		const hours	= this.state.selectedBooking.hours;
		const start	= moment().startOf('day').add(6, 'h');
		const end	= moment().startOf('day').add(1, 'd');

		let options	= [{label: "Vælg tid...", value: ""}];
		let from	= start.clone();
		let to		= from.clone().add(hours, 'h');

		do {
			options.push({
				label: `Fra Kl. ${from.format("HH:mm")} - ${to.format("HH:mm")}`,
				value: from.format("HH:mm")
			});

			from	= from.clone().add(1, 'h');
			to		= from.clone().add(hours, 'h');

		} while (to.isSameOrBefore(end));

		return options;
	}

	@computed
	get collection() {
		return decodeObservable(store.collection);
	}

    getNotes(booking) {
    	if (!booking || !booking.order) return null;

    	let notes = booking.order.notes || null;

    	if (!notes) return null;

    	notes = notes.trim();

		return notes.length > 0 ?
			notes :
			null;
    }

    getSubjects(booking) {
    	if (!booking || !booking.subjects) return null;

    	return booking.subjects.map(subject => subject.name || 'N/A').sort();
    }

    getRecipients(booking) {
    	if (!booking || !booking.order) return null;

        let recipients = [].concat(booking.order.subuserRecipients);

        if (booking.order.userRecipient) {
            recipients.unshift(booking.order.userRecipient);
        }

        return recipients;
    }

	handleAccept = (id) => {
		return store.update(id, {status: "accepted"});
	};

	handleCancel = (id) => {
		const booking = this.getBooking(id);
		if (!booking) return;

		const isAccepted = booking.accepted_at !== null;
		const hasFreePass = this.getBookingViewAs(booking) === 'bookable' && !userStore.get('has_cancelled_before');
		const phrase = isAccepted ?
			"aflyse" :
			"afvise";

		const title	= `Er du sikker på at du vil ${phrase}?`;
		const base	= `Er du sikker på at du vil ${phrase} denne booking?`;

		if (! isAccepted) {
			return this.confirmCancel(id, base, title);
		}

		if (hasFreePass) {
			let body = "Da det er første gang at du aflyser en booking, vil du ikke blive pålagt et aflysningsgebyr, men fremtidige aflysninger vil herefter blive pålagt et gebyr.";

			return this.confirmCancel(id, body, title, hasFreePass);
		}

		store.getFee(id)
			.catch(() => {
				let body = base + " Du kan komme til at betale et gebyr.";

				return this.confirmCancel(id, body, title);
			})
			.then(fee => {
				let body = base + " Der vil blive pålagt et gebyr for aflysningen på: " + moneyFormat(fee);

				return this.confirmCancel(id, body, title);
			});
	};

	confirmCancel(id, body, title, updateUser = false) {
		return this.confirm
			.await(body, title)
			.then(() => store.update(id, {status: "cancelled"}))
			.then(() => updateUser ? userStore.refresh() : null);
	}

	handleInfo = (id) => {
		const booking = this.collection.filter(booking => booking.id === id) [0];

		this.setState({
			selectedBooking: booking
		}, () => {
			this.infoModal.open();
		});
	}

	handleChange = (id) => {
		const booking = this.collection.filter(booking => booking.id === id) [0];
		const from = moment(booking.from);
		const date = from.isBefore(moment().startOf('day')) ?
			moment() :
			from.clone();

		this.setState({
			selectedBooking: booking,
			dateChange: date.format('YYYY-MM-DD')
		}, () => {
			this.changeModal.open();
			this.setState({timeOptions: this.getTimeOptions()});
		});
	};

	handleAcceptRequest = (id) => {
		return this.confirm
			.await("Er du sikker på at du vil acceptere denne ændring?")
			.then(() => store.update(id, {request_status: "accepted"}));
	};

	handleRejectRequest = (id) => {
		return this.confirm
			.await("Er du sikker på at du vil afvise denne ændring?")
			.then(() => store.update(id, {request_status: "rejected"}))
	};

	handleInfoModalClose = () => {
		this.setState({
			selectedBooking: null,
		});
	}

	handleChangeModalClose = () => {
		this.setState({
			selectedBooking: null,
			dateChange: null,
			timeChange: null,
			messageChange: null,
			timeOptions: [],
			changeModalLoading: false,
			changeModalError: null
		});
	};

	handleDateChange = (value) => {
		this.setState({dateChange: value});
	};

	handleTimeChange = (e) => {
		this.setState({
			timeChange: e.target.value,
			changeModalError: null
		});
	};

	handleMessageChange = (e) => {
		this.setState({
			messageChange: e.target.value
		});
	};

	onCancelChange = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.changeModal.close();
	};

	onSubmitChange = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.setState({changeModalLoading: true});

		// prepare data
		const date = this.state.dateChange ? moment(this.state.dateChange) : null;
		const time = this.state.timeChange ? moment(this.state.timeChange, "HH:mm") : null;
		const message = this.state.messageChange;
		const id = this.state.selectedBooking.id;

		store
			.update(id, {
				id: id,
				change: {
					from: date && time ? copyTime(date, time).format("YYYY-MM-DD HH:mm:ss") : null,
					message
				}
			})
			.then(() => {
				this.setState({changeModalLoading: false}, () => {
					this.changeModal.close();
				});
			})
			.catch(xhr => {
				const errors = xhr.response.data.errors;
				let error;

				if (typeof errors === "object") {
					error = errors[Object.keys(errors) [0]] [0];
				}

				this.setState({changeModalLoading: false, changeModalError: error});
			});
	};

	renderBookings() {
		return this.collection
			.filter(book => {
				return this.state.filter === 'received' ?
					book.bookable.id === userStore.get('id') :
					book.booker.id === userStore.get('id');
			})
			.map(booking => {
				return (
					<Booking key={booking.id} viewAs={this.getBookingViewAs(booking)}
									handleAcceptRequest={this.handleAcceptRequest}
									handleRejectRequest={this.handleRejectRequest}
									handleAccept={this.handleAccept}
									handleChange={this.handleChange}
									handleInfo={this.handleInfo}
									handleCancel={this.handleCancel} {...booking}/>
				);
		});
	}

	renderRecipient = (recipient, index) => {
		const name = recipient.name || null;

		const age = recipient.birthday ?
			moment().diff(recipient.birthday, 'years') :
			null;

		const level = recipient.level ?
			recipient.level.name :
			null;

		const avatar = recipient.avatar ?
			recipient.avatar.url :
			router.url("img/avatar-placeholder.png");

		return (
			<div key={index} className="recipient">
				<div>
					<img src={avatar} />
				</div>
				<div>
					<p><span>Navn: </span>{recipient.name || ''}</p>
					<p><span>Alder: </span>{age}</p>
					<p><span>Niveau/Klassetrin: </span>{level}</p>
				</div>
			</div>
		);
	}

	renderInfoModal() {
		const notes = this.getNotes(this.state.selectedBooking);
		const notesMarkup = notes ? (
				<React.Fragment>
					<h4>Noter</h4>
					<p>{notes}</p>
				</React.Fragment>
			) : null;

		const subjects = this.getSubjects(this.state.selectedBooking);
		const subjectsMarkup = subjects ? (
				<React.Fragment>
					<h4>Fag</h4>
					{subjects.map((sub, index) => (<p key={index}>{sub}</p>))}
				</React.Fragment>
			) : null;

		const recipients = this.getRecipients(this.state.selectedBooking);
		const recipientMarkup = recipients ? 
			recipients.map(this.renderRecipient) :
			null;

		return (
			<Modal ref={el => this.infoModal = el} handleClose={this.handleInfoModalClose}>
				<div className="top-bar">
					<p className="title">Booking information</p>
				</div>

				<div className="content">
					{notesMarkup}
					{subjectsMarkup}

					<h4>Undervisningsmodtager</h4>
					<div className="recipient-wrap">
						{recipientMarkup}
					</div>
				</div>
			</Modal>
		);
	}

	renderChangeModal() {
		const personName = this.state.selectedBooking ?
			this.getBookingPerson(this.state.selectedBooking).nickname :
			null;

		const responsive = !this.state.changeModalLoading;
		const className = classList(
			"change-booking-modal",
			this.state.changeModalLoading && "loading"
		);

		const errorMarkup = this.state.changeModalError ?
			(<p className="error">{this.state.changeModalError}</p>) : null;

		const selectClassName = this.state.timeOptionsLoading ? "loading" : null;

		return (
			<Modal ref={el => this.changeModal = el} className={className} closeable={responsive}
						 handleClose={this.handleChangeModalClose}>

				<div className="top-bar">
					<p className="title">Forespørg {personName} om et <span>nyt</span> tidspunkt</p>
				</div>

				<div className="modal-fields">

					<DatePicker value={this.state.dateChange}
											min={moment().toISOString()}
											label="Vælg dato"
											handleChange={this.handleDateChange}>

						<img src={router.url("img/icons/choose-date.svg")} alt="Choose date"/>

					</DatePicker>

					<Select className={selectClassName} value={this.state.timeChange || ""} options={this.state.timeOptions}
									label="Vælg ønsket tidspunkt" onChange={this.handleTimeChange}>
						<img src={router.url("img/icons/clock.svg")} alt="Choose time"/>

						<div className="loader">
							<svg className="circle-loader" width="40" height="40" version="1.1" xmlns="http://www.w3.org/2000/svg">
								<circle cx="20" cy="20" r="8"/>
							</svg>
						</div>

					</Select>

					<Textarea value={this.state.messageChange || ""} label="Din besked"
										onChange={this.handleMessageChange}/>
				</div>

				<div className="modal-actions">
					{errorMarkup}
					<div className="modal-buttons">
						<p>Giv meget gerne en begrundelse for din ændring.</p>
						<div className="btns">
							<button type="button" className="btn" onClick={this.onCancelChange} disabled={!responsive}>Annuller
								forespørgsel
							</button>
							<button type="button" className="btn btn-primary" onClick={this.onSubmitChange} disabled={!responsive}>
								Send
								forespørgsel
							</button>
						</div>
					</div>
				</div>

				<div className="loader">
					<svg className="circle-loader" width="40" height="40" version="1.1" xmlns="http://www.w3.org/2000/svg">
						<circle cx="20" cy="20" r="8"/>
					</svg>
				</div>

			</Modal>
		);
	}

	handleFilter(filter) {
		return () => this.setState({filter: filter});
	}

	renderFilter() {
		if (this.props.viewAs !== 'bookable') return null;

		return (
			<div className="bookings-filter">
				<button className={`btn ${this.state.filter === 'received' ? 'active' : ''}`}
						onClick={this.handleFilter('received')}>Bookinger som underviser</button>
				<button className={`btn ${this.state.filter === 'sent' ? 'active' : ''}`}
						onClick={this.handleFilter('sent')}>Bookinger som elev</button>
			</div>
		);
	}

	render() {
		return (
			<React.Fragment>
				{this.renderFilter()}
				<div className={this.className}>
					{this.renderBookings()}
					{this.renderChangeModal()}
					{this.renderInfoModal()}
					<Confirm ref={el => this.confirm = el}/>
				</div>
			</React.Fragment>
		);
	}

}

module.exports = BookingList;



// WEBPACK FOOTER //
// ./resources/js/components/backend/bookings/BookingList.js